import { cn, Flex, Text, DataTable } from "@suns/design-system";
import bigBoards from "./big-board-ranks.json";
import { useAsync } from "@/shared/hooks";
import { playersLoader } from "./loaders/player-loader";
import { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
// eslint-disable-next-line no-restricted-imports -- DO NOT COPY THIS LINE. See docs/lints.md.
import { PlayerRow } from "@suns/api/generated-client/apollo";
import { TeamLogo } from "@/components";
import { getLabel } from "@/shared/utils";

type DraftPlayerRow = PlayerRow & {
  scoutRank: number;
  consensus: {
    rank: number | null;
    min: number | null;
    max: number | null;
  };
};

const picks = [10, 29, 52, 59];

const columns: ColumnDef<DraftPlayerRow>[] = [
  {
    header: "Rank",
    accessorKey: "scoutRank",
    cell: (cell) => {
      const rank = cell.getValue<number | null>();
      return (
        <Flex align="center" gap="xs">
          {picks.includes(rank ?? 0) ? (
            <Text size="xs" heading>
              {rank !== null ? rank : "--"}
            </Text>
          ) : (
            <Text size="xs">{rank !== null ? rank : "--"}</Text>
          )}
        </Flex>
      );
    },
    enableSorting: false,
  },
  {
    header: "Player",
    accessorKey: "name",
    accessorFn: (row: DraftPlayerRow) => row.lastName + ", " + row.firstName,
    enableSorting: false,
  },
  {
    header: "Team",
    accessorKey: "currentTeam",
    cell: (cell) => {
      const teams = cell.row.original.currentTeams;
      return (
        <Flex direction="right" align="center" justify="center" gap="sm">
          {teams?.map((team, i) => (
            <Flex direction="right" align="center" gap="xs" key={team.id}>
              <TeamLogo
                src={team.image}
                nbaTeamId={team.nbaId || team.id}
                size="4xs"
              />
              <Text size="xs" key={team.id} className="text-nowrap">
                {team.code ? team.code : team.name!.slice(0, 3).toUpperCase()}
                {i < teams.length - 1 ? "," : ""}
              </Text>
            </Flex>
          ))}
        </Flex>
      );
    },
    enableSorting: false,
    accessorFn: (row: PlayerRow) =>
      row.currentTeams.map((team) => team.code).join(", "),
  },
  {
    header: "Position",
    accessorKey: "position",
    enableSorting: false,
    accessorFn: (row: PlayerRow) => {
      const position = row.metadata?.[0]?.hybridPosition;
      return position ? getLabel(position) : row.position;
    },
  },
  {
    header: "Analytics",
    accessorKey: "analyticsRank",
    cell: (cell) => {
      const rank = cell.getValue<number | null>();
      return <Text size="xs">{rank !== null ? rank : "--"}</Text>;
    },
    enableSorting: false,
  },
  {
    header: "Consensus",
    accessorKey: "consensusRank",
    cell: (cell) => {
      const { consensus } = cell.row.original;
      if (!consensus || consensus.rank === null) {
        return <Text>--</Text>;
      }
      return (
        <Text size="xs">
          {consensus.min === consensus.max
            ? consensus.min
            : `${consensus.min}-${consensus.max}`}
        </Text>
      );
    },
    enableSorting: false,
  },
];

export function PlayersDraftBigBoardPrintTable() {
  const { response, loading } = useAsync(playersLoader, {
    offset: 0,
    limit: 100,
    "player.in": bigBoards.scoutBigBoard.map((player) =>
      String(player.playerId)
    ),
  });

  const players = useMemo(() => {
    return response?.players
      .map((player) => {
        const scoutRank =
          bigBoards.scoutBigBoard.findIndex(
            (rankedPlayer) => rankedPlayer.playerId === player.id
          ) + 1;

        const consensusPlayer = bigBoards.consensusBigBoard.find(
          (rankedPlayer) => rankedPlayer.playerId === player.id
        );

        const consensusRank =
          bigBoards.consensusBigBoard.findIndex(
            (rankedPlayer) => rankedPlayer.playerId === player.id
          ) + 1;

        const analyticsRank =
          bigBoards.analyticsBigBoard.find(
            (rankedPlayer) => rankedPlayer.playerId === player.id
          )?.rank ?? 0;

        return {
          ...player,
          scoutRank: scoutRank > 0 ? scoutRank : null,
          analyticsRank: analyticsRank > 0 ? analyticsRank : null,
          consensus: {
            rank: consensusRank > 0 ? consensusRank : null,
            min: consensusPlayer?.min ?? null,
            max: consensusPlayer?.max ?? null,
          },
        };
      })
      .sort((player1, player2) => {
        let res = 0;
        if (player1.scoutRank === null) res = 1;
        else if (player2.scoutRank === null) res = -1;
        else res = player1.scoutRank - player2.scoutRank;
        return res;
      });
  }, [response]);

  const pickClassNames = picks.reduce<Record<number, string>>((acc, pick) => {
    acc[pick - 2] = "bg-purple-50";
    acc[pick - 1] = "bg-purple-100";
    acc[pick] = "bg-purple-200";
    acc[pick + 1] = "bg-purple-100";
    acc[pick + 2] = "bg-purple-50";
    return acc;
  }, {});

  return (
    <Flex direction="down" gap="md" className="print:gap-0">
      <Flex
        direction="down"
        className="h-letter w-letter break-inside-avoid bg-white p-[0.4in]"
        gap="md"
      >
        <Text size="xl" heading>
          Phoenix Suns 2025 Big Board
        </Text>
        <DataTable
          columns={columns}
          data={players?.slice(0, 33) as DraftPlayerRow[]}
          loading={loading}
          className={cn(
            ["[&_td]:px-2", "[&_td]:py-1", "[&_td]:text-xs"],
            ["[&_th]:p-2", "[&_th]:text-xs"]
          )}
          rowClassName={(_, index) => {
            const className = pickClassNames[index + 1];
            return cn(
              className,
              "border-gray-300",
              index == 29 && "border-b-2 border-gray-500"
            );
          }}
        />
      </Flex>
      <Flex
        direction="down"
        className="h-letter w-letter break-inside-avoid bg-white p-[0.4in]"
      >
        <DataTable
          columns={columns}
          data={players?.slice(33, players.length) as DraftPlayerRow[]}
          loading={loading}
          className={cn(
            ["[&_td]:px-2", "[&_td]:py-1", "[&_td]:text-xs"],
            ["[&_th]:p-2", "[&_th]:text-xs"]
          )}
          rowClassName={(_, index) => {
            const className = pickClassNames[index + 34];
            return cn(
              className,
              "border-gray-300",
              index == 59 - 34 && "border-b-2 border-gray-500"
            );
          }}
        />
      </Flex>
    </Flex>
  );
}
